import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";

export default function LeadNew() {
  const nav = useNavigate();
  const [meta, setMeta] = useState(null);
  const [f, setF] = useState({ source: "Instagram" });
  const [dups, setDups] = useState([]);
  const [deptTouched, setDeptTouched] = useState(false);

  useEffect(() => { api.get("/leads/meta").then(r=>{
    setMeta(r.data);
    // Default department: first one the user has access to (Study for admissions users, Filing for filing users, Study for admin)
    const allowed = r.data.allowed_departments || ["STUDY","FILING"];
    setF(o => ({ ...o, department: o.department || (allowed[0] || "STUDY") }));
  }); }, []);
  // Auto-suggest department from selected app type until the user overrides it
  useEffect(() => {
    if (deptTouched || !meta || !f.app_type_id) return;
    const t = (meta.app_types||[]).find(x => x.id === f.app_type_id);
    if (t?.default_department) setF(o => ({ ...o, department: t.default_department }));
  }, [f.app_type_id, meta, deptTouched]);
  useEffect(() => {
    const phone = (f.phone||"").trim(); const email = (f.email||"").trim();
    if (!phone && !email) { setDups([]); return; }
    const t = setTimeout(async () => {
      const params = new URLSearchParams();
      if (phone) params.set("phone", phone);
      if (email) params.set("email", email);
      try { const r = await api.get(`/leads/check-duplicate?${params.toString()}`); setDups(r.data.duplicates); }
      catch { setDups([]); }
    }, 400);
    return () => clearTimeout(t);
  }, [f.phone, f.email]);

  if (!meta) return <div className="text-slate-500">Loading…</div>;

  const set = (k, v) => setF(o => ({ ...o, [k]: v }));
  const submit = async () => {
    if (!f.full_name || !f.phone || !f.app_type_id) { toast.error("Name, phone, and file type are required"); return; }
    if (!f.department) { toast.error("Department (Study or Filing) is required"); return; }
    try { const r = await api.post("/leads", f); toast.success(`Created ${r.data.lead_id}`); nav(`/leads/${r.data.id}`); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const allowedDepts = (meta?.allowed_departments && meta.allowed_departments.length ? meta.allowed_departments : ["STUDY","FILING"]);

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div>
        <h1 className="heading-font text-3xl font-bold text-slate-900">New Lead</h1>
        <p className="text-sm text-slate-500 mt-1">Capture a query in under 30 seconds. Assign the caller now or leave it for the manager.</p>
      </div>

      {dups.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
          <div className="flex items-center gap-2 text-amber-900 font-semibold"><AlertTriangle className="w-4 h-4"/>Possible duplicate</div>
          <ul className="mt-2 space-y-1 text-amber-900">
            {dups.map(d => <li key={d.id}>· {d.lead_id} — {d.full_name} · Stage: {d.stage}</li>)}
          </ul>
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <F label="Full Name *"><Input data-testid="lead-full-name" value={f.full_name||""} onChange={e=>set("full_name", e.target.value)}/></F>
        <F label="Phone *"><Input data-testid="lead-phone" value={f.phone||""} onChange={e=>set("phone", e.target.value)}/></F>
        <F label="Email"><Input data-testid="lead-email" type="email" value={f.email||""} onChange={e=>set("email", e.target.value)}/></F>
        <F label="Query About / File Type *">
          <Select value={f.app_type_id||""} onValueChange={v=>set("app_type_id", v)}>
            <SelectTrigger data-testid="lead-type"><SelectValue placeholder="Choose type"/></SelectTrigger>
            <SelectContent>{meta.app_types.map(t=><SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
          </Select>
        </F>
        <F label="Department *">
          <Select value={f.department||""} onValueChange={v=>{ setDeptTouched(true); set("department", v); }}>
            <SelectTrigger data-testid="lead-department"><SelectValue placeholder="Study or Filing"/></SelectTrigger>
            <SelectContent>{allowedDepts.map(d=><SelectItem key={d} value={d}>{d === "STUDY" ? "Study (Admissions)" : "Filing (Immigration)"}</SelectItem>)}</SelectContent>
          </Select>
          <p className="text-[11px] text-slate-500 mt-1">Auto-suggested from the File Type — override anytime. Only your team will see this lead.</p>
        </F>
        <F label="Lead Source">
          <Select value={f.source} onValueChange={v=>set("source", v)}>
            <SelectTrigger data-testid="lead-source"><SelectValue/></SelectTrigger>
            <SelectContent>{meta.sources.map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </F>
        <F label="Assigned Caller">
          <Select value={f.assigned_caller_id||""} onValueChange={v=>set("assigned_caller_id", v)}>
            <SelectTrigger data-testid="lead-caller"><SelectValue placeholder="Unassigned"/></SelectTrigger>
            <SelectContent>{meta.callers.map(u=><SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}</SelectContent>
          </Select>
        </F>
        <F label="Next Follow-Up"><Input type="datetime-local" value={f.next_followup_at||""} onChange={e=>set("next_followup_at", e.target.value)}/></F>
        <F label="Consultation"><Input type="datetime-local" value={f.consultation_at||""} onChange={e=>set("consultation_at", e.target.value)}/></F>
        <div className="sm:col-span-2"><F label="Notes"><Textarea rows={3} value={f.notes||""} onChange={e=>set("notes", e.target.value)}/></F></div>
      </div>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={()=>nav("/leads")}>Cancel</Button>
        <Button data-testid="lead-create" onClick={submit} className="bg-orange-500 hover:bg-orange-600 text-white">Create Lead</Button>
      </div>
    </div>
  );
}
const F = ({ label, children }) => (<div className="space-y-1.5"><Label className="text-xs text-slate-600">{label}</Label>{children}</div>);
